<template>
  <div class="flex items-start justify-start">
    <div class="w-full p-4">
      <form @submit.prevent="saveNetworksBroker"> <!-- Formulario -->
        <div class="flex flex-col sm:flex-row mt-4 sm:mt-10">
          <div class="w-full sm:w-1/2 mb-5">    
            <div class="flex justify-center items-center text-2xl font-bold">
              Redes sociales de asesor
            </div>
            <!-- Contenido del primer div -->
            <div 
              v-for="(item, index) in networks_broker"
              :key="item.id"
              class="flex mt-5"
            >
              <div class="w-[20%] sm:w-[15%] p-2 flex justify-center items-center">
                <div v-html="item.icon" />
              </div>

              <div class="w-[80%] sm:w-[75%] p-2">
                <input
                  type="url" 
                  v-model="networks_broker[index].link_broker"
                  :placeholder="getPlaceholder(item.name)"
                  size="sm"
                  class="w-full px-4 py-[9px] bg-white rounded-md text-neutral-500 text-sm font-light font-['Poppins'] leading-normal border border-gray-200"   
                  pattern="https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)"
                  title="Ingrese una URL válida comenzando con http:// o https://"           
                />
                <p v-if="urlErrors[index]" class="text-red-500 text-xs mt-1">{{ urlErrors[index] }}</p>
              </div>
            </div>

            <div class="flex mt-5 justify-center items-center gap-2">
              <button 
                type="button" 
                class="cancel-btn"
                @click="goBack"
              >
                <span class="cancel-btn-text">Cancelar</span>
              </button>
              <button 
                type="submit" 
                class="custom-create-btn"
              >
                <span class="custom-create-btn-text">Guardar</span>
              </button>
            </div>         
          </div>
        </div>
      </form>

      <!-- Mensaje de éxito -->
      <div v-if="msg" class="user-alert">
        <div class="user-alert-content">
          <div class="user-alert-text">
            {{ msg }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import { useRoute, useRouter } from "vue-router";

let networks_broker = ref([]);
const route = useRoute();
const router = useRouter();
const advisorId = computed(() => route.params.id);  // Obtener el ID del rol desde la URL
let msg = ref("");
let urlErrors = ref([]);

const store = useStore();
let session = computed(() => store.getters.getUserSession);

// Función para volver atrás
const goBack = () => {
  router.go(-1); // Esto lleva a la página anterior
};

onMounted(async () => {
  try {
    await getNetworks();    
  } catch (error) {
    console.error("Error al intentar traer los datos", error);
  }
});

const placeholders = {
  facebook: "https://facebook.com/tu-perfil",
  twitter: "https://x.com/tu-perfil",
  instagram: "https://instagram.com/tu-perfil",
  linkedin: "https://linkedin.com/company/tu-perfil",
  youtube: "https://youtube.com/c/tu-perfil",
  tiktok: "https://www.tiktok.com/@tu-perfil",
};

const getPlaceholder = (networkName) => {
  return placeholders[networkName.toLowerCase()] || "https://ejemplo.com";
};

async function getNetworks() {
  try {
    let options = {
      headers: {
        Authorization: `Bearer ${session.value.token_api}`,
      },
    };

    const response = await axios.get(
      `${process.env.VUE_APP_API_BASE_URL}/broker/profile/networks/${advisorId.value}`,
      options
    );

    if (response.status == 401) {
      store.dispatch("clearUser Session");
    }

    if (response.status == 200) {
      networks_broker.value = response.data.data;
      urlErrors.value = Array(networks_broker.value.length).fill(''); // Inicializar errores
    }
  } catch (error) {
    console.error("Error fetching data:", error);
  }
}

// Función para validar URL
function isValidURL(url) {
  const urlPattern = new RegExp("https?://(www\\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)");
  return urlPattern.test(url);
}

async function saveNetworksBroker() {
  try {
    let options = {
      headers: {
        'Authorization': `Bearer ${session.value.token_api}`
      }
    };

    let params_item = [];
    urlErrors.value = []; // Reiniciar errores

    networks_broker.value.forEach((item, index) => {
      let row = {};
      row['id'] = item.id;
      row['link_broker'] = item.link_broker; // Mantener el enlace, incluso si está vacío
      row['user_id'] = advisorId.value;
      row['company_id'] = session.value.company_id;

      // Validar URL solo si no está vacío
      if (item.link_broker && !isValidURL(item.link_broker)) {
        urlErrors.value[index] = 'URL no válida';
      } else {
        urlErrors.value[index] = ''; // Limpiar error si es válido
        params_item.push(row); // Agregar el registro a params_item
      }
    });

    // Si hay errores, no continuar
    if (urlErrors.value.some(error => error)) {
      return; // Detener la ejecución si hay errores
    }

    let params = {
      type: "broker",
      step: 3,
      networks: params_item
    };

    const response = await axios.put(
      `${process.env.VUE_APP_API_BASE_URL}/broker/profile/${advisorId.value}/update-networks`,
      params,
      options
    );

    if (response.status == 200) {
      store.dispatch('updateStep', 3);
      msg.value = 'Redes sociales guardadas correctamente'; // Mostrar mensaje de éxito

      // Limpiar el mensaje después de 5 segundos
      setTimeout(() => {
        msg.value = '';
      }, 3000);
    }

  } catch (error) {
    console.error("Error fetching data:", error);
    msg.value = 'No se pudo guardar las redes sociales en este momento'; // Mostrar mensaje de error
  }
}
</script>

<style scoped>
.cancel-btn {
  padding: 0.5rem 1.5rem;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
}

.cancel-btn-text {
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
}

.custom-create-btn {
  padding: 0.5rem 1.5rem;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
}

.custom-create-btn-text {
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
}

.gap-2 {
  gap: 0.5rem;
}

.text-red-500 {
  color: #ef4444;
}

.text-xs {
  font-size: 0.75rem;
  line-height: 1rem;
}

.mt-1 {
  margin-top: 0.25rem;
}

.user-alert {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: 10px;
  border-radius: 5px;
  margin-top: 10px;
}

.user-alert-content {
  display: flex;
  align-items: center;
}

.user-alert-text {
  font-family: 'Poppins', sans-serif;
  font-size: 0.875rem;
}
</style>